import {
  Button,
  Input,
  Section,
  SectionBody,
  SectionHeader,
  Textarea,
} from '@apex/component-library'
import {
  Box,
  Checkbox,
  FormControl,
  FormErrorMessage,
  HStack,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from '@chakra-ui/react'
import { get, isEmpty } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { Link as ReactRouterLink } from 'react-router-dom'
import { FaExternalLinkAlt } from 'react-icons/fa'
import { TransferInitiationTransferType } from '../../api/types'
import { AccountDetails, AccountOwner, Correspondent } from '../../types'
import { RoutesEnum } from '../../types/RoutesEnum'
import { ContextualLink } from '../ContextualLink'
import InputCounter from '../InputCounter/InputCounter'
import { CashAssetsForm } from './CashAssets'
import { OtherSecuritiesForm } from './OtherSecurities'
import { AccountTransferTable, findAndFormatJointOwnerTaxIds } from './'
import { findTaxIdTypeAndFormatTaxId } from './taxIdUtils'
import TransferSubmissionInfo from './TransferSubmissionInfo'
import AssetsAlert from './AssetsAlert'
import { TransferInitiation } from '../InitiateTransferPage/types'
import { OptionsForm } from './Options'
import { OptionsError } from './Options/types'
import { OtherSecuritiesErrors } from './OtherSecurities/types'

export interface AccountTransferFormProps {
  accountDetails: AccountDetails
  correspondent: Correspondent
  deliveringAccount: TransferInitiation
  accountOwners: AccountOwner[]
  formikErrors: {
    clientReferenceId?: string
    contraParty?: {
      account?: string
      participant?: {
        participantNumber?: string
      }
      accountTitle?: string
      comment?: string
    }
    cash?: string
    options?: OptionsError[]
    assets?: OtherSecuritiesErrors[]
  }
  formikTouched: {
    clientReferenceId?: boolean
    contraParty?: {
      account?: boolean
      participant?: {
        participantNumber?: boolean
      }
      accountTitle?: boolean
      comment?: boolean
    }
  }
  handleChange: (e: React.ChangeEvent) => void
  handleBlur: (e: { target: { name: string } }) => void
  setFieldValue: (field: string, value: any) => void
  handleSubmit: () => void
}

const AccountTransferForm = ({
  accountDetails: {
    accountNumber: receivingAccountNumber,
    accountTitle: receivingAccountTitle,
    taxId: receivingPrimaryTaxId,
  },
  correspondent: { name: correspondentName, code: correspondentCode },
  deliveringAccount: {
    transferType,
    contraParty: {
      account: deliveringAccountNumber,
      participant: { participantNumber: deliveringParticipantId },
      accountTitle: deliveringAccountTitle,
      primarySsnOrTaxId: deliveringPrimarySsnOrTaxId,
      secondarySsnOrTaxId: deliveringSecondarySsnOrTaxId,
      comment: memo,
    },
    clientReferenceId,
    cash,
    assets,
    options,
  },
  accountOwners,
  handleBlur,
  handleChange,
  setFieldValue,
  handleSubmit,
  formikErrors,
  formikTouched,
}: AccountTransferFormProps) => {
  const getFieldError = (fieldPath: string) =>
    get(formikTouched, fieldPath) ? get(formikErrors, fieldPath) : undefined
  const setFieldValueMemo = useCallback(setFieldValue, [])
  const [isAssetsAlertOpen, setAssetsAlertOpen] = useState(false)

  /* Setting the default transferType here instead of formik's initialValues to allow toggling back to the original
  value to work.  Form persistence relies on formik.dirty, which is only true when values differ from the original
  initialValues, so toggling to PARTIAL_TRANSFER_RECEIVER worked but changing back to FULL_TRANSFER wasn't since that
  was the initial value before it was ever changeable.
  After updating the form persistence logic to recognize the updates, I chose this much simpler route instead. */
  useEffect(() => {
    if (!transferType) {
      setFieldValueMemo(
        'transferType',
        TransferInitiationTransferType.FullTransfer
      )
    }
  }, [transferType, setFieldValueMemo])

  const clientReferenceIdError = getFieldError('clientReferenceId')
  const accountError = getFieldError('contraParty.account')
  const participantNumberError = getFieldError(
    'contraParty.participant.participantNumber'
  )
  const accountTitleError = getFieldError('contraParty.accountTitle')
  const commentError = getFieldError('contraParty.comment')
  const isSubmitDisabled =
    !isEmpty(formikErrors) ||
    isEmpty(deliveringAccountNumber) ||
    isEmpty(deliveringParticipantId) ||
    isEmpty(deliveringAccountTitle)

  const receivingPrimaryTaxIdWithType = findTaxIdTypeAndFormatTaxId(
    accountOwners,
    receivingPrimaryTaxId
  )
  const receivingSecondaryTaxIds = findAndFormatJointOwnerTaxIds(accountOwners)

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (transferType === TransferInitiationTransferType.FullTransfer) {
      handleSubmit()
    } else {
      const noAssetsPresent =
        isEmpty(cash) && isEmpty(assets) && isEmpty(options)
      if (noAssetsPresent) {
        setAssetsAlertOpen(true)
      } else {
        handleSubmit()
      }
    }
  }

  const closeAssetsAlert = () => {
    setAssetsAlertOpen(false)
  }

  const performFullTransfer = () => {
    setFieldValueMemo(
      'transferType',
      TransferInitiationTransferType.FullTransfer
    )
    closeAssetsAlert()
    handleSubmit()
  }

  return (
    <form onSubmit={onSubmit}>
      <Box>
        <AccountTransferTable
          title="Account Transfer Request"
          transferType={
            <RadioGroup
              name="transferType"
              onChange={e => {
                setFieldValue('transferType', e)
              }}
              defaultValue={TransferInitiationTransferType.FullTransfer}
              value={transferType}
              sx={{
                span: {
                  fontWeight: 'regular',
                },
              }}
            >
              <Stack>
                <Radio value={TransferInitiationTransferType.FullTransfer}>
                  <Text>
                    <span style={{ fontWeight: 600 }}>Full Transfer</span> -
                    Transfer all assets from delivering account.
                  </Text>
                </Radio>
                <Radio
                  value={TransferInitiationTransferType.PartialTransferReceiver}
                >
                  <Text>
                    <span style={{ fontWeight: 600 }}>Partial Transfer</span> -
                    Choose assets to transfer from delivering account.
                  </Text>
                </Radio>
              </Stack>
            </RadioGroup>
          }
          deliveringAccountNumber={
            <FormControl isInvalid={!!accountError}>
              <HStack>
                <Input
                  isInvalid={!!accountError}
                  isRequired
                  name="contraParty.account"
                  value={deliveringAccountNumber}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  maxLength={20}
                />
                <InputCounter
                  characterCount={deliveringAccountNumber.length}
                  maxCharacterCount={20}
                />
              </HStack>
              <FormErrorMessage>{accountError}</FormErrorMessage>
            </FormControl>
          }
          receivingAccountNumber={receivingAccountNumber}
          deliveringParticipantId={
            <FormControl isInvalid={!!participantNumberError}>
              <HStack>
                <Input
                  isInvalid={!!participantNumberError}
                  isRequired
                  name="contraParty.participant.participantNumber"
                  value={deliveringParticipantId}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  width="25%"
                  maxLength={4}
                />
                <ContextualLink
                  as={ReactRouterLink}
                  to={`/${RoutesEnum.ParticipantIds}`}
                  icon={FaExternalLinkAlt}
                  isExternal
                >
                  Common Participant IDs
                </ContextualLink>
              </HStack>
              <FormErrorMessage>{participantNumberError}</FormErrorMessage>
            </FormControl>
          }
          receivingParticipantId={`${correspondentName.replace(
            `(${correspondentCode})`,
            ''
          )} (0158)`}
          deliveringAccountTitle={
            <FormControl isInvalid={!!accountTitleError}>
              <HStack>
                <Input
                  isInvalid={!!accountTitleError}
                  isRequired
                  name="contraParty.accountTitle"
                  value={deliveringAccountTitle}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  maxLength={60}
                />
                <InputCounter
                  characterCount={deliveringAccountTitle.length}
                  maxCharacterCount={60}
                />
              </HStack>
              <FormErrorMessage>{accountTitleError}</FormErrorMessage>
            </FormControl>
          }
          receivingAccountTitle={receivingAccountTitle}
          deliveringPrimaryTaxIdWithType={
            <Checkbox
              fontWeight={400}
              isChecked={!!deliveringPrimarySsnOrTaxId}
              name="contraParty.primarySsnOrTaxId"
              onChange={e =>
                setFieldValue(
                  'contraParty.primarySsnOrTaxId',
                  e.target.checked ? '999999999' : null
                )
              }
              mb="0"
            >
              No TIN / SSN for Owner
            </Checkbox>
          }
          receivingPrimaryTaxIdWithType={receivingPrimaryTaxIdWithType}
          deliveringSecondaryTaxId={
            <Checkbox
              fontWeight={400}
              isChecked={!!deliveringSecondarySsnOrTaxId}
              name="contraParty.secondarySsnOrTaxId"
              onChange={e =>
                setFieldValue(
                  'contraParty.secondarySsnOrTaxId',
                  e.target.checked ? '999999999' : null
                )
              }
            >
              No TIN / SSN for Secondary
            </Checkbox>
          }
          receivingSecondaryTaxIds={receivingSecondaryTaxIds}
          clientReferenceId={
            <HStack>
              <FormControl isInvalid={!!clientReferenceIdError}>
                <Input
                  isInvalid={!!clientReferenceIdError}
                  name="clientReferenceId"
                  value={clientReferenceId}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  maxLength={255}
                />
                <FormErrorMessage>{clientReferenceIdError}</FormErrorMessage>
              </FormControl>
              <InputCounter
                characterCount={clientReferenceId.length}
                maxCharacterCount={255}
              />
            </HStack>
          }
          memo={
            <HStack alignItems="flex-start">
              <FormControl isInvalid={!!commentError}>
                <Textarea
                  isInvalid={!!commentError}
                  name="contraParty.comment"
                  value={memo}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  maxLength={255}
                />
                <FormErrorMessage>{commentError}</FormErrorMessage>
              </FormControl>
              <InputCounter
                characterCount={memo.length}
                maxCharacterCount={255}
                pt="10px"
              />
            </HStack>
          }
        />
        {transferType ===
          TransferInitiationTransferType.PartialTransferReceiver && (
          <Section
            data-testid="assets-section"
            marginTop="1.4286rem"
            width="60%"
          >
            <SectionHeader>Assets</SectionHeader>
            <SectionBody>
              <CashAssetsForm
                marginTop="1rem"
                formikErrors={formikErrors.cash}
                setFieldValue={setFieldValue}
                cash={cash}
              />
              <OptionsForm
                errors={formikErrors.options}
                setFieldValue={setFieldValue}
                options={options}
              />
              <OtherSecuritiesForm
                errors={formikErrors.assets}
                assets={assets}
                setFieldValue={setFieldValue}
              />
            </SectionBody>
          </Section>
        )}
        <TransferSubmissionInfo marginY="1.4286rem" />
        <Button variant="primary" type="submit" disabled={isSubmitDisabled}>
          Review Transfer Request
        </Button>
      </Box>
      <AssetsAlert
        isOpen={isAssetsAlertOpen}
        onCancel={closeAssetsAlert}
        onConfirm={performFullTransfer}
      />
    </form>
  )
}

export default AccountTransferForm
